import {
  ChevronRight,
  FileText,
  User,
  LogOut,
  Menu,
  X,
  Loader2,
} from "lucide-react";
import { menuItems } from "../../pages/User/Dashboard";
import { useViewClass } from "@/hooks/viewClass";
import { toTitleCase } from "@/utils/string";

const ClassDetail: React.FC = () => {
  const {
    isSidebarOpen,
    isCollapsed,
    user,
    course,
    assignments,
    isLoading,
    defaultImage,
    id,
    navigate,
    handleLogout,
    handleSidebarClick,
    setIsSidebarOpen,
    setIsCollapsed,
  } = useViewClass();

  return (
    <div className="flex min-h-screen bg-white font-sans text-gray-900 relative">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 bottom-0 left-0 z-50 bg-white border-r border-gray-100 flex flex-col 
          transform transition-all duration-300 ease-in-out overflow-hidden
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          lg:translate-x-0 lg:static lg:h-screen lg:inset-y-0
          ${isCollapsed ? "lg:w-[80px]" : "lg:w-64"} 
        `}
      >
        <div
          className={`
            h-20 flex items-center mb-2 transition-all duration-300 w-full
            ${isCollapsed ? "justify-center px-0" : "justify-between px-6"}
          `}
        >
          {!isCollapsed && (
            <img
              src="/Fluenesia.png"
              alt="Fluenesia Logo"
              className="h-6 w-auto object-contain block"
            />
          )}

          <button
            onClick={() => {
              if (window.innerWidth < 1024) setIsSidebarOpen(false);
              else setIsCollapsed(!isCollapsed);
            }}
            className={`
              text-gray-600 hover:text-[#B21318] transition rounded-lg hover:bg-gray-100 focus:outline-none flex items-center justify-center p-2
              ${!isCollapsed ? "mr-2" : ""}
            `}
          >
            <div className="lg:hidden">
              <X size={24} />
            </div>
            <div className="hidden lg:block">
              <Menu size={24} strokeWidth={2.5} />
            </div>
          </button>
        </div>

        <nav className="flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-4">
          <p
            className={`text-[10px] font-bold text-black uppercase mb-2 mt-4 tracking-wider transition-all duration-300 ${isCollapsed ? "text-center text-[9px]" : "px-2"}`}
          >
            Dashboard
          </p>

          {menuItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleSidebarClick(item.name)}
              title={isCollapsed ? item.name : ""}
              className={`
                group w-full flex items-center py-3 rounded-2xl font-medium transition-all duration-300 relative
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
                ${item.name === "My Class" ? "bg-[#FF0000] text-white shadow-md shadow-red-200" : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"}
              `}
            >
              <div className="shrink-0">{item.icon}</div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                {item.name}
              </span>

              {isCollapsed && (
                <div className="absolute left-14 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">
                  {item.name}
                </div>
              )}
            </button>
          ))}

          <div className="pt-6">
            <p
              className={`text-[10px] font-bold text-black uppercase mb-2 tracking-wider transition-all duration-300 ${isCollapsed ? "text-center text-[9px]" : "px-2"}`}
            >
              Account
            </p>
            <button
              onClick={() => navigate("/user/dashboard")}
              title={isCollapsed ? "Profile" : ""}
              className={`
                group w-full flex items-center py-3 rounded-2xl font-medium transition-all duration-300
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
                text-gray-400 hover:bg-gray-50 hover:text-gray-600
              `}
            >
              <div className="shrink-0">
                <User size={20} />
              </div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                Profile
              </span>
            </button>
            <button
              onClick={handleLogout}
              title={isCollapsed ? "Logout" : ""}
              className={`
                w-full flex items-center py-3 rounded-2xl font-medium text-red-600 mt-2 hover:bg-red-50 transition-all duration-300
                ${isCollapsed ? "justify-center px-0" : "justify-start px-4 gap-3"}
              `}
            >
              <div className="shrink-0">
                <LogOut size={20} />
              </div>
              <span
                className={`text-sm whitespace-nowrap transition-all duration-300 ${isCollapsed ? "w-0 opacity-0 hidden" : "w-auto opacity-100 block"}`}
              >
                Logout
              </span>
            </button>
          </div>
        </nav>
      </aside>

      <main
        className={`flex-1 w-full bg-white min-h-screen transition-all duration-300 ${isCollapsed ? "lg:ml-[80px]" : "lg:ml-auto"}`}
      >
        <header className="flex justify-between lg:justify-end p-4 lg:p-6 items-center sticky top-0 bg-white/90 backdrop-blur-sm z-30 border-b lg:border-none border-gray-100">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg lg:hidden"
          >
            <Menu size={24} />
          </button>
          {user && (
            <div className="flex items-center gap-3 px-2 lg:px-4 py-2 rounded-full hover:bg-gray-50 transition-all cursor-pointer">
              <div className="flex flex-col items-end leading-tight">
                <span className="font-bold text-sm text-gray-800 text-right whitespace-nowrap">
                  {user.name}
                </span>
                <span className="text-[10px] text-gray-500 uppercase font-medium">
                  {user.role || "Student"}
                </span>
              </div>
              <div className="w-9 h-9 bg-[#B21318] rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm uppercase shrink-0">
                {user.name.charAt(0)}
              </div>
            </div>
          )}
        </header>

        <div className="max-w-5xl mx-auto px-4 lg:px-8 pb-12">
          <div className="flex items-center gap-2 text-sm font-medium mb-4 mt-2">
            <span
              className="text-[#FF0000] cursor-pointer hover:underline"
              onClick={() =>
                navigate("/user/dashboard", { state: { tab: "My Class" } })
              }
            >
              My Class
            </span>
            <span className="text-[#FF0000]"> ▸ </span>

            <span className="text-gray-600 truncate max-w-[200px]">
              {course ? toTitleCase(course.title) : "Loading..."}
            </span>
          </div>

          <div className="mb-6">
            <p className="text-gray-500 text-sm mb-1">Classroom</p>
            <h1 className="text-3xl font-bold text-gray-900">
              {course ? toTitleCase(course.title) : "Loading Course..."}
            </h1>
          </div>

          <div className="w-full h-[200px] bg-red-50 rounded-2xl relative overflow-hidden mb-10 flex items-center px-10">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FFEBEE] to-[#FFCDD2] opacity-80"></div>
            <img
              src={defaultImage}
              alt="Course Banner"
              className="absolute bottom-0 right-0 w-[400px] object-contain object-right-bottom opacity-90"
            />
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-start justify-between mb-12">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                About Program
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm">
                {course
                  ? `Welcome to the ${course.title} course. This program is designed for ${course.level} level students to improve their skills efficiently. Guided by our expert tutor, ${course.tutor_name}, you will master the materials step by step.`
                  : "Loading program description..."}
              </p>
            </div>
            <div>
              <button
                onClick={() => navigate(`/user/class/${id}/materials`)}
                className="bg-[#FF0000] hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg shadow-sm transition-colors text-sm"
              >
                View Materials
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-2xl bg-white shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">
              Assignment List
            </h3>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Loader2
                  size={32}
                  className="animate-spin mb-2 text-[#FF0000]"
                />
                <p className="text-sm">Loading assignments...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-xl">
                <p>No assignments history found.</p>
                <button
                  onClick={() => navigate("/assessment/free-test")}
                  className="mt-2 text-[#FF0000] font-bold text-sm hover:underline"
                >
                  Start a new test
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {assignments.map((item, index) => {
                  const isDone = item.status === "Done";
                  return (
                    <div
                      key={item.id}
                      className={`flex flex-col md:flex-row items-center gap-4 py-4 ${index !== assignments.length - 1 ? "border-b border-gray-100" : ""}`}
                    >
                      <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-[#FF0000]" />
                      </div>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 w-full items-center">
                        <div className="col-span-1">
                          <p className="text-xs text-gray-400 mb-0.5">
                            {item.type}
                          </p>
                          <p className="font-bold text-gray-900">
                            {item.title}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <p className="text-xs text-gray-400 mb-0.5">
                            {isDone ? "Completed" : "Deadline"}
                          </p>
                          <p className="text-sm font-medium text-gray-700">
                            {item.deadline}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <p className="text-xs text-gray-400 mb-1">Status</p>
                          <span
                            className={`px-3 py-1 rounded-full text-[10px] font-bold ${isDone ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <div className="col-span-1 flex flex-col md:flex-row gap-6">
                          <div>
                            <p className="text-xs text-gray-400 mb-0.5">
                              Grade
                            </p>
                            <p className="text-sm font-bold text-gray-900">
                              {item.grade}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-400 mb-0.5">
                              Notes
                            </p>
                            <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                              {item.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0 mt-4 md:mt-0">
                        <button className="bg-[#FF0000] hover:bg-red-700 text-white font-bold py-2 px-8 rounded-full text-xs transition-colors shadow-red-100 shadow-lg">
                          {item.action}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClassDetail;
